import React, { useState, useEffect, useRef } from 'react';
import { Accordion, AccordionItem, AccordionItemButton, AccordionItemHeading, AccordionItemPanel } from 'react-accessible-accordion';
import { HexColorInput, HexAlphaColorPicker } from 'react-colorful';
import getFonts from '../../Functions/config/fonts';
import getSelectField from '../fields/select';
import toggleColorPicker from '../../Functions/toggleColorPicker';
import Container from './affordability/containerStyling';
import Header from './affordability/header';
import Layout from './affordability/layout';
import FieldStyles from './affordability/FieldStyles';
import ButtonSettings from './affordability/buttons';
import ResultsDisplay from './affordability/resultdisplay';
import InfoPopup from '../InfoPopup';

const Affordability = (props) => {
    const { widget: propsWidget, selectedEditor, updateWrapperWidget, previewWidget } = props;
    const { htmlData } = propsWidget;

    const affordabilityRef = useRef(null);

    // Info contents
    const advancedInfoContent = `
        <h2>Advanced Settings</h2>
        <p>These settings control how the affordability calculator evaluates a user's ability to afford a home based on their income and debts:</p>
        <ul>
          <li><strong>Total Debt-to-Income Ratio (DTI):</strong> This is the maximum percentage of your gross monthly income that can be spent on all debt payments, including housing costs (like mortgage payments) and other debts (e.g., car loans, credit card payments). For example, if set to 36%, up to 36% of your monthly income can go toward all debts. A lower DTI means stricter affordability criteria, while a higher DTI allows more debt but may indicate higher financial risk.</li>
          <li><strong>Housing Debt-to-Income Ratio:</strong> This is the maximum percentage of your gross monthly income that can be spent on housing expenses alone (e.g., mortgage payments, property taxes, and insurance). For example, if set to 28%, only 28% of your monthly income can be used for housing costs. This ratio ensures housing expenses don’t overwhelm your budget, and it’s typically lower than the Total DTI to account for other debts.</li>
        </ul>
        <p><strong>How They Work:</strong> The calculator uses these ratios to determine the maximum home price and monthly payment you can afford. It calculates your monthly income (annual income ÷ 12 + extra income) and applies the Housing DTI to find the maximum housing payment. The Total DTI limits the total debt payments (housing + other debts). The lower of these two limits is used to calculate the affordable mortgage, factoring in your down payment, loan term, and interest rate.</p>
    `;

    const resultsInfoContent = `
        <h2>Results Display</h2>
        <p>The Results Section shows the outcome of the affordability calculation after the user enters their financial details and clicks "Calculate." It helps users understand what home price they can afford and whether a specific home price is within their budget. The section is hidden by default and appears only after a valid calculation. Design changes (e.g., background color, text color) made in the editor are applied when the results are shown, but you must perform a calculation to see them.</p>
    `;

    const getFieldConfig = (label, required = true) => {
        return {
            label,
            placeholder: `Enter ${label.toLowerCase()}`,
            ...htmlData?.affordability?.fields?.[label.toLowerCase().replace(' ', '')]
        };
    };

    const getButtonConfig = (text, bgColor, hoverColor, textColor) => {
        return {
            text,
            textColor,
            bgColor,
            hoverColor,
            borderRadius: "4px",
            fontSize: "16px",
            fontWeight: "bold",
            fontFamily: "Arial, sans-serif"
        };
    };

    const [widget, setWidget] = useState({
        htmlData: {
            affordability: {
                container: {
                    bgColor: "#ffffff",
                    borderColor: "#dee2e6",
                    borderRadius: "8px",
                    padding: "20px",
                    shadow: "0 2px 10px rgba(0,0,0,0.1)"
                },
                header: {
                    heading: "Affordability Calculator",
                    subHeading: "See how much home you can afford",
                    fontFamily: "Arial, sans-serif",
                    fontSize: "24px",
                    fontWeight: "bold",
                    textAlign: "center",
                    textColor: "#333333",
                },
                layout: {
                    template: 'vertical',
                    spacing: 'normal'
                },
                fields: {
                    textColor: "#333333",
                    bgColor: "#ffffff",
                    hoverColor: "#2980b9",
                    fontSize: "16px",
                    fontWeight: "normal",
                    labelFontSize: "14px",
                    labelFontWeight: "bold",
                    textAlign: "left",
                    fontFamily: "Arial, sans-serif",
                    homePrice: getFieldConfig("Home Price"),
                    annualIncome: getFieldConfig("Annual Income"),
                    extraIncome: getFieldConfig("Extra Income"),
                    monthlyDebts: getFieldConfig("Monthly Debts"),
                    downPayment: getFieldConfig("Down Payment"),
                    loanTerm: getFieldConfig("Loan Term"),
                    interestRate: getFieldConfig("Interest Rate")
                },
                button: {
                    calculate: getButtonConfig("Calculate", "#3498db", "#2980b9", "#ffffff"),
                    reset: getButtonConfig("Reset", "#f8f9fa", "#e9ecef", "#333333")
                },
                results: {
                    layout: 'simple',
                    bgColor: "#f8f9fa",
                    textColor: "#2c3e50",
                    borderColor: "#e9ecef",
                    fontFamily: "Arial, sans-serif"
                },
                defaults: {
                    homePrice: "",
                    annualIncome: "",
                    extraIncome: "",
                    monthlyDebts: "",
                    downPayment: "",
                    loanTerm: "30",
                    interestRate: "4.5"
                },
                advanced: {
                    debtToIncomeRatio: 36,
                    housingDebtToIncomeRatio: 28
                },
                disclaimer: {
                    text: "Results are estimates only. Consult with a financial advisor for personalized advice.",
                    textColor: "#6c757d",
                    fontFamily: "Arial, sans-serif"
                },
                ...htmlData?.affordability
            }
        },
        widgetType: propsWidget.widgetType.id,
        widgetTypeName: propsWidget.widgetType.name,
        widgetTypeScripts: propsWidget.widgetType.scripts,
        title: propsWidget.title || '',
        expires: propsWidget.expires || '',
        FirstLoad: true
    });

    const handleInputChange = async (e, path = []) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? (checked ? 'on' : 'off') : value;
        const newWidget = JSON.parse(JSON.stringify(widget));
        let target = newWidget;

        if (name === 'title') {
            target.title = val;
        } else {
            target = newWidget.htmlData.affordability;
            path.forEach((p, i) => {
                if (i === path.length - 1) {
                    target[p][name] = val;
                } else {
                    target = target[p] = { ...target[p] };
                }
            });
        }
        newWidget.FirstLoad = false;
        setWidget(newWidget);

        name === 'title' ? await updateWrapperWidget?.(newWidget) : await previewWidget?.(newWidget);
    };

    const setColorsAndPreview = async (section, stateItem, value) => {
        const path = section.split('.');
        const newWidget = JSON.parse(JSON.stringify(widget));
        let target = newWidget.htmlData.affordability;

        for (let i = 0; i < path.length - 1; i++) {
            target = target[path[i]];
        }

        target[path[path.length - 1]][stateItem] = value;

        newWidget.FirstLoad = false;
        setWidget(newWidget);
        await previewWidget?.(newWidget);
    };

    const fontFamilyOptions = getFonts().map(font => ({ value: font, label: font }));

    useEffect(() => {
        const loadPreview = async () => {
            await previewWidget?.(widget);
        };
        loadPreview();
    }, [widget]);

    const { affordability } = widget.htmlData;

    // Function to handle opening the popup with specific info
    const openPopup = (info) => {
        if (affordabilityRef.current) {
            affordabilityRef.current.openPopup(info);
        }
    };

    return (
        <div className="mjwi-editor-main-inner">
            {/* Render InfoPopup and attach ref */}
            <InfoPopup ref={affordabilityRef} />

            {selectedEditor === 'Builder' && (
                <div className="mjwi-editor-builder">
                    <label htmlFor="title">Widget Name</label>
                    <input
                        type="text"
                        name="title"
                        value={widget.title}
                        placeholder="Title"
                        onChange={handleInputChange}
                        className="mjwi-input"
                    />

                    <Accordion allowZeroExpanded>
                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>Header</AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <div className="mjwi-row">
                                        <div className="mjwi-col33">
                                            <label htmlFor="heading">Heading</label>
                                        </div>
                                        <div className="mjwi-col66">
                                            <input
                                                type="text"
                                                name="heading"
                                                value={affordability.header.heading}
                                                onChange={(e) => handleInputChange(e, ['header'])}
                                                className="mjwi-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="mjwi-row">
                                        <div className="mjwi-col33">
                                            <label htmlFor="subHeading">Sub Heading</label>
                                        </div>
                                        <div className="mjwi-col66">
                                            <input
                                                type="text"
                                                name="subHeading"
                                                value={affordability.header.subHeading}
                                                onChange={(e) => handleInputChange(e, ['header'])}
                                                className="mjwi-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>

                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>Input Fields</AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    {Object.keys(affordability.fields).filter(key => !['textColor', 'bgColor', 'hoverColor', 'fontSize', 'fontWeight', 'labelFontSize', 'labelFontWeight', 'textAlign', 'fontFamily'].includes(key)).map(fieldKey => (
                                        <React.Fragment key={fieldKey}>
                                            <div className="mjwi-row">
                                                <div className="mjwi-col33">
                                                    <label>{`${fieldKey.charAt(0).toUpperCase() + fieldKey.replace(/([A-Z])/g, ' $1').slice(1)} Label`}</label>
                                                </div>
                                                <div className="mjwi-col66">
                                                    <input
                                                        type="text"
                                                        name="label"
                                                        value={affordability.fields[fieldKey].label}
                                                        onChange={(e) => handleInputChange(e, ['fields', fieldKey])}
                                                        className="mjwi-input"
                                                    />
                                                </div>
                                            </div>
                                            <div className="mjwi-row">
                                                <div className="mjwi-col33">
                                                    <label>{`${fieldKey.charAt(0).toUpperCase() + fieldKey.replace(/([A-Z])/g, ' $1').slice(1)} Placeholder`}</label>
                                                </div>
                                                <div className="mjwi-col66">
                                                    <input
                                                        type="text"
                                                        name="placeholder"
                                                        value={affordability.fields[fieldKey].placeholder}
                                                        onChange={(e) => handleInputChange(e, ['fields', fieldKey])}
                                                        className="mjwi-input"
                                                    />
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>

                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>Default Values</AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <div className="mjwi-row">
                                        <div className="mjwi-col50">
                                            <label htmlFor="homePrice">Home Price</label>
                                        </div>
                                        <div className="mjwi-col50">
                                            <input
                                                type="text"
                                                name="homePrice"
                                                value={affordability.defaults.homePrice}
                                                onChange={(e) => handleInputChange(e, ['defaults'])}
                                                className="mjwi-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="mjwi-row">
                                        <div className="mjwi-col50">
                                            <label htmlFor="annualIncome">Annual Income</label>
                                        </div>
                                        <div className="mjwi-col50">
                                            <input
                                                type="text"
                                                name="annualIncome"
                                                value={affordability.defaults.annualIncome}
                                                onChange={(e) => handleInputChange(e, ['defaults'])}
                                                className="mjwi-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="mjwi-row">
                                        <div className="mjwi-col50">
                                            <label htmlFor="extraIncome">Extra Income</label>
                                        </div>
                                        <div className="mjwi-col50">
                                            <input
                                                type="text"
                                                name="extraIncome"
                                                value={affordability.defaults.extraIncome}
                                                onChange={(e) => handleInputChange(e, ['defaults'])}
                                                className="mjwi-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="mjwi-row">
                                        <div className="mjwi-col50">
                                            <label htmlFor="monthlyDebts">Monthly Debts</label>
                                        </div>
                                        <div className="mjwi-col50">
                                            <input
                                                type="text"
                                                name="monthlyDebts"
                                                value={affordability.defaults.monthlyDebts}
                                                onChange={(e) => handleInputChange(e, ['defaults'])}
                                                className="mjwi-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="mjwi-row">
                                        <div className="mjwi-col50">
                                            <label htmlFor="downPayment">Down Payment</label>
                                        </div>
                                        <div className="mjwi-col50">
                                            <input
                                                type="text"
                                                name="downPayment"
                                                value={affordability.defaults.downPayment}
                                                onChange={(e) => handleInputChange(e, ['defaults'])}
                                                className="mjwi-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="mjwi-row">
                                        <div className="mjwi-col50">
                                            <label htmlFor="loanTerm">Loan Term (years)</label>
                                        </div>
                                        <div className="mjwi-col50">
                                            <select
                                                name="loanTerm"
                                                value={affordability.defaults.loanTerm}
                                                onChange={(e) => handleInputChange(e, ['defaults'])}
                                                className="mjwi-select"
                                            >
                                                <option value="15">15</option>
                                                <option value="20">20</option>
                                                <option value="30">30</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="mjwi-row">
                                        <div className="mjwi-col50">
                                            <label htmlFor="interestRate">Interest Rate (%)</label>
                                        </div>
                                        <div className="mjwi-col50">
                                            <input
                                                type="number"
                                                name="interestRate"
                                                value={affordability.defaults.interestRate}
                                                onChange={(e) => handleInputChange(e, ['defaults'])}
                                                step="0.1"
                                                min="0"
                                                max="20"
                                                className="mjwi-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>

                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>
                                    Advanced
                                    <i
                                        onClick={() => openPopup(advancedInfoContent)}
                                        className="fa-solid fa-circle-info"
                                    ></i>
                                </AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <div className="mjwi-row">
                                        <div className="mjwi-col50">
                                            <label htmlFor="debtToIncomeRatio">Total Debt-to-Income Ratio (%)</label>
                                        </div>
                                        <div className="mjwi-col50">
                                            <input
                                                type="number"
                                                name="debtToIncomeRatio"
                                                value={affordability.advanced.debtToIncomeRatio}
                                                onChange={(e) => handleInputChange(e, ['advanced'])}
                                                min="0"
                                                max="100"
                                                step="1"
                                                className="mjwi-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="mjwi-row">
                                        <div className="mjwi-col50">
                                            <label htmlFor="housingDebtToIncomeRatio">Housing Debt-to-Income Ratio (%)</label>
                                        </div>
                                        <div className="mjwi-col50">
                                            <input
                                                type="number"
                                                name="housingDebtToIncomeRatio"
                                                value={affordability.advanced.housingDebtToIncomeRatio}
                                                onChange={(e) => handleInputChange(e, ['advanced'])}
                                                min="0"
                                                max="100"
                                                step="1"
                                                className="mjwi-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>

                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>Disclaimer</AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <div className="mjwi-row">
                                        <div className="mjwi-col33">
                                            <label htmlFor="text">Disclaimer Text</label>
                                        </div>
                                        <div className="mjwi-col66">
                                            <textarea
                                                name="text"
                                                value={affordability.disclaimer.text}
                                                onChange={(e) => handleInputChange(e, ['disclaimer'])}
                                                className="mjwi-textarea"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>
                    </Accordion>
                </div>
            )}

            {selectedEditor === 'Layout' && (
                <div className="mjwi-editor-layout">
                    <Accordion allowZeroExpanded>
                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>Container</AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <Container
                                        widget={widget}
                                        handleInputChange={handleInputChange}
                                        setColorsAndPreview={setColorsAndPreview}
                                    />
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>
                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>Header</AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <Header
                                        widget={widget}
                                        handleInputChange={handleInputChange}
                                        setColorsAndPreview={setColorsAndPreview}
                                    />
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>
                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>Layout</AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <Layout
                                        widget={widget}
                                        handleInputChange={handleInputChange}
                                        setColorsAndPreview={setColorsAndPreview}
                                    />
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>
                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>Fields</AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <FieldStyles
                                        widget={widget}
                                        handleInputChange={handleInputChange}
                                        setColorsAndPreview={setColorsAndPreview}
                                    />
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>
                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>Button</AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <ButtonSettings
                                        widget={widget}
                                        handleInputChange={handleInputChange}
                                        setColorsAndPreview={setColorsAndPreview}
                                    />
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>
                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>
                                    Results Display
                                    <i
                                        onClick={() => openPopup(resultsInfoContent)}
                                        className="fa-solid fa-circle-info"
                                    ></i>
                                </AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <ResultsDisplay
                                        widget={widget}
                                        handleInputChange={handleInputChange}
                                        setColorsAndPreview={setColorsAndPreview}
                                    />
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>
                        <AccordionItem>
                            <AccordionItemHeading>
                                <AccordionItemButton>Disclaimer</AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                                <div className="mjwi-acc-panel-wrapper">
                                    <div className="mjwi-row">
                                        <div className="mjwi-col33">
                                            <label htmlFor="textColor">Text Color</label>
                                        </div>
                                        <div className="mjwi-col66 mjwi-clr-picker">
                                            <span
                                                className="clrPickerVal"
                                                style={{ backgroundColor: affordability.disclaimer.textColor }}
                                                onClick={toggleColorPicker}
                                            ></span>
                                            <HexAlphaColorPicker
                                                color={affordability.disclaimer.textColor}
                                                onChange={(color) => setColorsAndPreview('disclaimer', 'textColor', color)}
                                                style={{ display: 'none' }}
                                                className="mjwi-apfl-color-picker"
                                            />
                                            <HexColorInput
                                                color={affordability.disclaimer.textColor}
                                                onChange={(color) => setColorsAndPreview('disclaimer', 'textColor', color)}
                                            />
                                        </div>
                                    </div>
                                    <div className="mjwi-row">
                                        <div className="mjwi-col33">
                                            <label htmlFor="fontFamily">Font Family</label>
                                        </div>
                                        <div className="mjwi-col66">
                                            {getSelectField('fontFamily', fontFamilyOptions.map(opt => opt.label), affordability.disclaimer.fontFamily || 'Arial, sans-serif', (e) => handleInputChange(e, ['disclaimer']))}
                                        </div>
                                    </div>
                                </div>
                            </AccordionItemPanel>
                        </AccordionItem>
                    </Accordion>
                </div>
            )}
        </div>
    );
};

export default Affordability;